/**
 * Schedule Roll-Up Dashboard
 * Shows roll-up status across all macro activities with KPIs, 
 * breakdown table, and batch roll-up controls.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, CheckCircle2, Clock, AlertTriangle, Link2, Scale,
  ArrowUpCircle, BarChart3, ChevronRight, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_STYLES = {
  SYNCED: { color: '#22C55E', bg: '#22C55E12', label: 'Synced', icon: CheckCircle2 },
  STALE: { color: '#F59E0B', bg: '#F59E0B12', label: 'Stale', icon: Clock },
  PARTIAL: { color: '#F97316', bg: '#F9731612', label: 'Partial', icon: AlertTriangle },
  UNLINKED: { color: '#6B7280', bg: '#6B728012', label: 'Unlinked', icon: Link2 },
};

function KPICard({ label, value, total, color, icon: Icon }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-center gap-2 mb-1">
        <div className="p-1.5 rounded-lg" style={{ background: `${color}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <div className="text-xl font-bold" style={{ color }}>{value}</div>
      {total > 0 && <div className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{pct}% of {total}</div>}
    </div>
  );
}

function ProgressBar({ value, max = 100, color = '#3B82F6', height = 6 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="rounded-full overflow-hidden" style={{ height, backgroundColor: 'var(--border-color)' }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function RollUpDashboard({ projectId, onSelectMacro }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [batchRunning, setBatchRunning] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data: d } = await api.get(`/v1/projects/${projectId}/schedule/macro/rollup-summary`);
      setData(d);
    } catch (e) {
      // Auto-seed if no data
      try {
        await api.post(`/v1/projects/${projectId}/schedule/rollup/seed`);
        const { data: d } = await api.get(`/v1/projects/${projectId}/schedule/macro/rollup-summary`);
        setData(d);
      } catch {}
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleBatchRollup = async () => {
    setBatchRunning(true);
    try {
      const { data: r } = await api.post(`/v1/projects/${projectId}/schedule/rollup/batch/${projectId}`, {});
      toast.success(`Batch roll-up: ${r.rolledUp} activities updated, ${r.errors} errors`);
      fetchSummary();
    } catch { toast.error('Batch roll-up failed'); }
    setBatchRunning(false);
  };

  const s = data?.summary || {};
  const activities = data?.activities || [];
  const filtered = filter === 'ALL' ? activities : activities.filter(a => a.rollUpStatus === filter);

  return (
    <div className="space-y-4" data-testid="rollup-dashboard">
      {/* Control Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4" style={{ color: '#143A6B' }} />
          <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Schedule Roll-Up Dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchSummary} data-testid="rollup-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={handleBatchRollup} disabled={batchRunning || s.stale === 0} data-testid="rollup-batch-btn">
            {batchRunning ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />}
            Roll Up All Stale ({s.stale || 0})
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-6 gap-3">
        <KPICard label="Total Activities" value={s.total || 0} total={0} color="#6B7280" icon={BarChart3} />
        <KPICard label="Synced" value={s.synced || 0} total={s.total} color="#22C55E" icon={CheckCircle2} />
        <KPICard label="Stale" value={s.stale || 0} total={s.total} color="#F59E0B" icon={Clock} />
        <KPICard label="Partial" value={s.partial || 0} total={s.total} color="#F97316" icon={AlertTriangle} />
        <KPICard label="Unlinked" value={s.unlinked || 0} total={s.total} color="#6B7280" icon={Link2} />
        <KPICard label="Weight Issues" value={s.weightIssues || 0} total={s.total} color="#EF4444" icon={Scale} />
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1">
        {['ALL', 'SYNCED', 'STALE', 'PARTIAL', 'UNLINKED'].map(f => {
          const st = STATUS_STYLES[f] || { color: '#6B7280' };
          return (
            <button key={f} onClick={() => setFilter(f)}
              className="text-[10px] px-3 py-1 rounded-lg transition-all font-medium"
              style={{
                backgroundColor: filter === f ? (st.bg || '#6B728012') : 'transparent',
                color: filter === f ? (st.color || '#6B7280') : 'var(--text-muted)',
              }}>
              {f === 'ALL' ? `All (${s.total || 0})` : `${f} (${s[f.toLowerCase()] || 0})`}
            </button>
          );
        })}
      </div>

      {/* Activities Table */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="rollup-activities-table">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: 'var(--hover-bg)' }}>
                <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Code</th>
                <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Activity Name</th>
                <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Linked</th>
                <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Status</th>
                <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Rolled-Up %</th>
                <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Source</th>
                <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Modules</th>
                <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Last Roll-Up</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => {
                const st = STATUS_STYLES[a.rollUpStatus] || STATUS_STYLES.UNLINKED;
                const StIcon = st.icon;
                return (
                  <tr key={a.id} className="hover:opacity-80 cursor-pointer transition-all"
                    style={{ borderBottom: '1px solid var(--border-color)' }}
                    onClick={() => onSelectMacro?.(a)} data-testid={`rollup-row-${a.wbsCode || a.id}`}>
                    <td className="p-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{a.wbsCode || '—'}</td>
                    <td className="p-2 max-w-[240px] truncate" style={{ color: 'var(--text-primary)' }}>{a.name}</td>
                    <td className="p-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{
                        backgroundColor: a.linkedMicroCount > 0 ? '#3B82F615' : 'var(--hover-bg)',
                        color: a.linkedMicroCount > 0 ? '#3B82F6' : 'var(--text-disabled)',
                      }}>{a.linkedMicroCount}</span>
                    </td>
                    <td className="p-2 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: st.bg, color: st.color }}>
                        <StIcon className="w-3 h-3" /> {st.label}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      {a.rolledUpPct != null ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{a.rolledUpPct}%</span>
                          {a.rollUpDelta != null && a.rollUpDelta !== 0 && (
                            <span className="text-[9px]" style={{ color: a.rollUpDelta > 0 ? '#22C55E' : '#EF4444' }}>
                              {a.rollUpDelta > 0 ? '+' : ''}{a.rollUpDelta}%
                            </span>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                    </td>
                    <td className="p-2 text-center">
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
                        backgroundColor: a.activeSource === 'ROLL_UP' ? '#143A6B15' : '#6B728012',
                        color: a.activeSource === 'ROLL_UP' ? '#143A6B' : '#6B7280',
                      }}>{a.activeSource === 'ROLL_UP' ? 'RU' : 'M'}</span>
                    </td>
                    <td className="p-2 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {(a.linkedModules || []).map(m => (
                          <span key={m} className="text-[9px] px-1 py-0.5 rounded" style={{
                            backgroundColor: m === 'DCI' ? '#3B82F615' : m === 'MCI' ? '#22C55E15' : m === 'QPT' ? '#F59E0B15' : '#143A6B15',
                            color: m === 'DCI' ? '#3B82F6' : m === 'MCI' ? '#22C55E' : m === 'QPT' ? '#F59E0B' : '#143A6B',
                          }}>{m}</span>
                        ))}
                      </div>
                    </td>
                    <td className="p-2 text-center text-[10px]" style={{ color: 'var(--text-disabled)' }}>
                      {a.lastRollUpAt ? new Date(a.lastRollUpAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-2"><ChevronRight className="w-3 h-3" style={{ color: 'var(--text-disabled)' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>No activities match the filter</div>
          )}
        </div>
      )}
    </div>
  );
}
